import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  getWebsocketAPISocket,
  isUserProvider,
  joinSocketRoom,
  onSocketEvent,
} from "@jasper/shared";
import {
  GET_NUMBER_OF_NOTIFICATIONS,
  COUNT_NUMBER_OF_NEW_CASES_FOR_STAFF,
} from "/@/gql/";
import { UseAuth } from "/@/contexts";
import { OrderStatus } from "../../../generated/graphql";
import { asUser } from "../helpers/navMenu";

const adjustCount =
  (setCount: React.Dispatch<React.SetStateAction<number>>) =>
  (action: string) => {
    if (action === "add") {
      setCount(val => val + 1);
    } else if (action === "remove") {
      setCount(val => Math.max(0, val - 1));
    }
  };

const makeLogger =
  (label: string) =>
  (...args: unknown[]) =>
    console.warn(`${label} in navbar.ts (labs)`, ...args);

export const useNavbarNotifications = () => {
  const { user } = UseAuth();

  const [chatNotification, setChatNotification] = useState<number>(0);
  const [helpNotification, setHelpNotification] = useState<number>(0);
  const [orderDesignNotification, setOrderDesignNotification] =
    useState<number>(0);
  const [designReviewNotification, setDesignReviewNotification] =
    useState<number>(0);
  const [newCases, setNewCases] = useState<number>(0);

  const { data: numberOfNotification, refetch: refetchNotificationCount } =
    useQuery(GET_NUMBER_OF_NOTIFICATIONS, {
      fetchPolicy: "network-only",
    });

  const { data: numberOfNewCases, refetch: refetchNumberOfNewCases } = useQuery(
    COUNT_NUMBER_OF_NEW_CASES_FOR_STAFF,
    {
      variables: {
        where: {
          status: {
            in: isUserProvider(asUser(user))
              ? [OrderStatus.WaitingForProduction]
              : [OrderStatus.Confirmed, OrderStatus.NeedToBeProcessed],
          },
          isArchived: {
            equals: false,
          },
        },
      },
      fetchPolicy: "network-only",
    }
  );

  useEffect(() => {
    const isProvider = isUserProvider(asUser(user));
    const pastillesRoom = isProvider
      ? "provider-pastilles-" + user.userGroupId
      : "admin-pastilles";

    const countSetterByType: Record<
      string,
      React.Dispatch<React.SetStateAction<number>>
    > = {
      ChatNotification: setChatNotification,
      NewOrderNotification: setNewCases,
      ...(isProvider
        ? {}
        : {
            HelpNotification: setHelpNotification,
            designTranslation: setOrderDesignNotification,
            designReview: setDesignReviewNotification,
          }),
    };

    const handlePastilles = (value: { type: string; action: string }) => {
      const setCount = countSetterByType[value.type];
      if (setCount) {
        adjustCount(setCount)(value.action);
      }
    };

    const handleDisconnect = makeLogger("Connection closed");
    const handleClose = makeLogger("Connection closed");
    const handleConnectError = makeLogger("Connection failed");
    const handleReconnectAttempt = makeLogger("Reconnection attempt");
    const handleReconnectError = makeLogger("Reconnection error");
    const handleReconnectFailed = makeLogger("Reconnection failed");
    const handlePing = makeLogger("Ping");
    const handleError = makeLogger("Error");
    const handleOpen = makeLogger("Opened web-socket");

    const handleReconnect = (attempt: number) => {
      refetchNumberOfNewCases();
      refetchNotificationCount();
      console.warn(
        `Successfully reconnected in navbar.ts (labs) after ${attempt} attempt(s)`
      );
    };

    const sock = getWebsocketAPISocket();

    const handleSocketLifecycleOn = () => {
      sock.on("disconnect", handleDisconnect);
      sock.io.on("close", handleClose);
      sock.on("connect_error", handleConnectError);
      sock.io.on("reconnect_attempt", handleReconnectAttempt);
      sock.io.on("reconnect_error", handleReconnectError);
      sock.io.on("reconnect_failed", handleReconnectFailed);
      sock.io.on("ping", handlePing);
      sock.io.on("error", handleError);
      sock.io.on("open", handleOpen);
      sock.io.on("reconnect", handleReconnect);
    };

    const handleSocketLifecycleOff = () => {
      sock.off("disconnect", handleDisconnect);
      sock.io.off("close", handleClose);
      sock.off("connect_error", handleConnectError);
      sock.io.off("reconnect_attempt", handleReconnectAttempt);
      sock.io.off("reconnect_error", handleReconnectError);
      sock.io.off("reconnect_failed", handleReconnectFailed);
      sock.io.off("ping", handlePing);
      sock.io.off("error", handleError);
      sock.io.off("open", handleOpen);
      sock.io.off("reconnect", handleReconnect);
    };

    const leavePastillesRoom = joinSocketRoom(pastillesRoom);
    const offPastilles = onSocketEvent<{ type: string; action: string }>(
      pastillesRoom,
      handlePastilles
    );
    handleSocketLifecycleOn();

    return () => {
      leavePastillesRoom();
      offPastilles();
      handleSocketLifecycleOff();
    };
  }, []);

  useEffect(() => {
    const totalCount = numberOfNewCases?.getAllOrdersForStaff?.totalCount;
    if (totalCount != null) {
      setNewCases(totalCount);
    }
  }, [numberOfNewCases]);

  useEffect(() => {
    if (numberOfNotification?.getNotificationCount) {
      setChatNotification(
        numberOfNotification.getNotificationCount.chatNotification
      );
      setHelpNotification(
        numberOfNotification.getNotificationCount.helpNotification
      );
      setOrderDesignNotification(
        numberOfNotification.getNotificationCount.orderDesignNotification
      );
      setDesignReviewNotification(
        numberOfNotification.getNotificationCount.designReviewNotification
      );
    }
  }, [numberOfNotification]);

  return {
    chatNotification,
    helpNotification,
    orderDesignNotification,
    designReviewNotification,
    newCases,
  };
};
